<template>
  <div class="action_end_box">
    <!-- 1. 映射关系看板 (Source -> Target) -->
    <div class="end_mapping_board">
      <div class="board_inner">
        <!-- 当前按键 -->
        <div class="key_slot source">
          <div class="slot_label">{{ $t('current_selected_key') }}</div>
          <div class="key_display active">{{ currentKeyDisp }}</div>
        </div>

        <!-- 连动装饰 -->
        <div class="mapping_flow">
          <div class="flow_icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <path d="M12 7v5l3 2" stroke-width="2" />
            </svg>
          </div>
          <div class="flow_label">END</div>
        </div>

        <!-- 目标按键 -->
        <trigger_btn class="key_slot target" :label="$t('mapping_action')" :vks="targetVks" :tab="tab" :show-edit="true" :large="true" @trigger-click="openDialog" />
      </div>
    </div>

    <!-- 2. 设置区块 -->
    <div class="end_settings_section" v-if="showDelayTime">
      <div class="mgx_setting_item_card">
        <div class="setting_header">
          <div class="header_left">
            <svg class="pill_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <path d="M12 6v6l4 2" stroke-width="2" stroke-linecap="round" />
            </svg>
            <span class="pill_title">{{ $t('action_set_delay_time') }}</span>
          </div>
          <span class="pill_value">{{ delayTime }}<span>ms</span></span>
        </div>
        <div class="setting_slider_container">
          <input v-model.number="delayTime" class="slider_input" type="range" max="1000" min="0" step="1" :style="{ backgroundSize: timeBackgroundSize }" />
          <div class="slider_labels">
            <span>0ms</span>
            <span>1000ms</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 按键选择弹窗 -->
    <action_key_dialog v-model:visible="showKeyDialog" :row-index="0" :base-tab="tab" :selected-keys="targetVks" @confirm="onKeyConfirm" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import trigger_btn from './trigger_btn.vue'
import action_key_dialog from './action_key_dialog.vue'
import { actionSet } from '@/service/driver/action_set'
import { SetType, BaseTabs } from '@/service/driver/action_types'
import { MagnetSeniorType } from '@libs/types/enum'
import { VK, kks } from '@/define/define'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { normalizeKeyCode } from '@/tool/utility'
import { operate } from '@/service/driver/operate'
import { currentSelectedKeyText } from './action_config'

const props = defineProps<{ tab: BaseTabs; keys: VK[] }>()
const showKeyDialog = ref(false)
const delayTime = ref(0)
let { Keys, KeyType, DelayTime } = toRefs(actionSet.magnetValue)

const currentKeyDisp = computed(() => currentSelectedKeyText.value)

/**
 * 显示用的目标按键 (单一数据源)
 * - 直接读取 magnetValue.Keys[0].KeyCode,不在 initData 里 mutate Keys
 * - 设备回包格式可能是 [vk,0,0] (主键在 index0),这里统一归一化为 [0,0,vk] 后给 trigger_btn,
 *   避免 trigger_btn 内 cleanVksForDisplay 在 !isSupportCompose 设备上把 index0/1 清零导致空白
 * - 这样可以避免在 initData 中通过 initBaseKeysBySwitchInput 写回 magnetValue.Keys[0].KeyCode
 *   带来的 deep watch 级联污染
 */
const targetVks = computed<VK[]>(() => {
  const code = actionSet.magnetValue.Keys?.[0]?.KeyCode
  if (!Array.isArray(code) || code.length === 0) return [0, 0, 0]
  // 用 normalizeKeyCode 把第一个非 0 主键移到 index 2,与全局展示约定保持一致
  return normalizeKeyCode(JSON.parse(JSON.stringify(code)), BaseTabs.base) as VK[]
})

const timeBackgroundSize = computed(() => {
  const timePercent = ((delayTime.value - 0) / (1000 - 0)) * 100
  return `${timePercent}% 100%`
})

const showDelayTime = ref(false)

/**
 * 同步 UI 状态到当前 magnetValue (不破坏现有 Keys 数据)
 * - 仅在 Keys 完全无效时才补一个空槽位,确保 onKeyConfirm 可写入
 * - 不再调用 initBaseKeysBySwitchInput,避免重复 mutate Keys[0].KeyCode
 *   导致 deep watch 级联以及与设备原始 [vk,0,0] 布局的冲突
 */
const initData = () => {
  if (!Array.isArray(Keys.value) || Keys.value.length === 0) {
    Keys.value = [
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [],
        Travel: 0,
      },
    ]
    DelayTime.value = 0
  }
  if (KeyType.value !== MagnetSeniorType.SeniorKeyTypeEND) {
    KeyType.value = MagnetSeniorType.SeniorKeyTypeEND
  }
  delayTime.value = actionSet.modeTimes[SetType.end]
}

onMounted(() => {
  initData()
  showDelayTime.value = specialCheckCurrent(speciaBehiver.SupportEndDelayTime)
})

watch(
  () => JSON.stringify(actionSet.magnetValue),
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      initData()
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => operate.currentKey.value,
  () => {
    initData()
  },
)

watch(delayTime, (val) => {
  const v = Number(val)
  actionSet.modeTimes[SetType.end] = v
  DelayTime.value = v
})

onUnmounted(() => {
  showDelayTime.value = false
})

/**
 * 打开映射按键选择弹窗
 * - 在打开瞬间同步 baseKeys 给 dialog 使用 (与 action_dks/action_mt 的 openKeyDialog 一致)
 * - 不再放在 initData 里调用,避免每次切键都 mutate magnetValue 触发级联
 */
const openDialog = () => {
  actionSet.initBaseKeysBySwitchInput(targetVks.value)
  showKeyDialog.value = true
}

const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  // 保存时只保留 1 个 Key 槽位 (设备 END 只用 Keys[0]),避免遗留旧模式的多槽位数据
  const prev = Keys.value?.[0]
  Keys.value = [
    {
      KeyCode: keys,
      KeyFunction: prev?.KeyFunction || [],
      Travel: prev?.Travel || 0,
    },
  ]
}
</script>

<style lang="less" scoped>
.action_end_box {
  margin: auto;
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;

  .end_mapping_board {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px);
    border-radius: var(--radius-l);
    padding: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.8);

    .board_inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 30px;
    }

    .key_slot {
      width: 130px;
      height: 80px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 12px 14px;
      border-radius: var(--radius-l);
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(0, 0, 0, 0.03);
      transition: var(--transition-base);

      .slot_label {
        font-size: 12px;
        color: var(--color-text-hint);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 2px;
      }

      .key_display {
        font-size: 22px;
        font-weight: 900;
        color: var(--color-dark);
        line-height: 1.2;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          color: var(--brand-color-pure);
        }
      }

      &.target {
        cursor: pointer;
        position: relative;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        padding: 0;
        height: 80px;

        &:hover {
          border-color: var(--brand-color-pure);
          box-shadow: var(--action-btn-shadow-hover);
          transform: translateY(-3px);
        }

        &.empty {
          .key_display {
            font-size: 14px;
            color: var(--color-text-hint);
            font-weight: 600;
          }
        }

        .edit_hint {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 14px;
          height: 14px;
          color: var(--color-text-hint);
          opacity: 0.4;
        }
      }
    }

    .mapping_flow {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;

      .flow_icon {
        width: 24px;
        height: 24px;
        color: var(--brand-color-pure);
        filter: drop-shadow(0 0 8px rgba(255, 124, 0, 0.3));
      }

      .flow_label {
        font-size: 9px;
        font-weight: 900;
        color: var(--brand-color-pure);
        opacity: 0.7;
      }
    }
  }

  .end_settings_section {
    width: 100%;
  }
}
</style>
